import { useMutation, useQueryClient } from "@tanstack/react-query";
import { financialAccountContractClient } from "./financialAccount.api";
import { organizationIntegrationQueryKeys } from "./organizationIntegration.api";

/**
 * Removes a single financial account from the firm integrations table.
 *
 * Scoped to one account rather than the whole login: the table's rows are
 * accounts, and removing a connection outright would take every sibling account
 * with it. The server drops the connection itself once its last account is gone,
 * so a one-account login still disappears entirely.
 *
 * `forceDeletion` is deliberately never sent — it discards manual journal
 * entries, which is not a decision this menu should make silently. Without it
 * the endpoint answers 409, and `backendClient`'s interceptor rejects and toasts
 * the server's message, so the caller only has to keep the dialog open.
 */
export function useRemoveOrgIntegrationAccountMutation(organizationId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (financialAccountId: number) =>
            await financialAccountContractClient.deleteAccount({
                params: { financialAccountId },
                query: {},
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: organizationIntegrationQueryKeys.all(organizationId),
            });
        },
    });
}
